import React from "react";
import { Layout } from "../../../shared/Layout/Layout";
import { Header } from "../../../shared/Header/Header";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { Footer } from "../../../shared/Footer/Footer";
import classes from "../styles.module.scss";
import { Skeleton } from "../../../../../../shared/Skeleton/Skeleton";
import { Input } from "../../../../../../shared/Input/Input";
import { useSubmit } from "../hooks/useSubmit";
import { useQuestionnaire } from "../../../hooks/useQuestionnaire";
import { useForm } from "../hooks/useForm";
import { Questions } from "../../../boxes/Questions/Questions";
import { useQuestionBuilderModal } from "../../../shared/ModalQuestionBuilder/hooks/useQuestionBuilderModal";
import { MultiQuestionBuilderModal } from "../shared/MultiQuestionnaireBuilderModal";
import { useQuestionSchema } from "../schemas";
import { getIsSomeLogicJumpsFilled, withPathParams } from "../utilities";
import { SETTINGS_ROUTES } from "../../../../../../consts/routes";
import { ButtonSwitch } from "../shared/ButtonSwitch";

export const MultiQuestionnaireCreateEdit = withPathParams(
  SETTINGS_ROUTES.questionnaires.multi.edit(":id"),
  "id",
)(({ questionnaireId }) => {
  const { tSettings } = useAppTranslation.Settings();

  const submit = useSubmit(questionnaireId);
  const questionnaire = useQuestionnaire(questionnaireId);
  const builderModal = useQuestionBuilderModal();
  const questionSchema = useQuestionSchema();

  const form = useForm({
    submitter: submit.initiate,
    questionnaire: questionnaire.data || {},
  });

  return (
    <Layout>
      <Header
        questionnaireId={questionnaireId}
        right={
          questionnaireId ? (
            <ButtonSwitch questionnaireId={questionnaireId} toProvider={true} />
          ) : undefined
        }
        title={
          questionnaireId
            ? tSettings("questionnaires.multi.edit")
            : tSettings("questionnaires.multi.create")
        }
      />
      <div className={classes.formBox}>
        {questionnaire.isFetching ? (
          <Skeleton count={1} height={48} />
        ) : (
          <Input
            name="title"
            value={form.values.title}
            onChange={form.handleChange}
            onBlur={form.onBlur}
            placeholder={tSettings("questionnaires.form.title")}
            isError={Boolean(form.getError("title"))}
          />
        )}
      </div>
      <Questions
        value={form.questions.value}
        onSortEnd={form.questions.replace}
        onQuestionAdd={() => builderModal.onOpen()}
        isLoading={questionnaire.isFetching}
      >
        {(q, i) => (
          <Questions.Tile
            key={i}
            index={i}
            question={q.question}
            type={tSettings(`questionnaires.multi.types.${q.questionType}`)}
            onDelete={() => form.questions.deleteByIndex(i)}
            onDuplicate={() => form.questions.duplicateByIndex(i)}
            isRequired={q.isRequired}
            description={q.description}
            isError={
              !questionSchema.isValidSync(q, {
                strict: true,
              }) || form.logicJumpErrorsQuestionIds.includes(q.id)
            }
            onEdit={() => builderModal.onOpen(q)}
            typeRightAdornment={
              getIsSomeLogicJumpsFilled(q.logicJumps) ? (
                <i className="fa fa-share-alt" aria-hidden="true"></i>
              ) : undefined
            }
          />
        )}
      </Questions>
      <Footer
        questionnaireId={questionnaireId}
        onSubmit={form.handleSubmit}
        isSubmitting={submit.isLoading}
        error={form.getError("questions")}
      />
      {builderModal.isOpen && (
        <MultiQuestionBuilderModal
          isOpen
          onAdd={(next) => {
            form.questions.append(next);
            builderModal.onClose();
          }}
          onUpdateByIndex={(...args) => {
            form.questions.updateByIndex(...args);
            builderModal.onClose();
          }}
          preloadedQuestion={builderModal.preloadedQuestion}
          onClose={builderModal.onClose}
          questions={form.questions.value}
        />
      )}
    </Layout>
  );
});
